import React, { useContext, useState } from "react";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import message from "antd/lib/message";
import Radio from "antd/lib/radio";
import Form from "antd/lib/form";
import { useMutation } from "react-query";
import { createApiClient } from "apiClient";
import { AppButton } from "components/button";
import { ButtonType } from "components/button/appButton";
import {
  PatientContext,
  PatientContextState
} from "contextApis/patientContext";
import { UserContext, UserContextState } from "contextApis/userContext";
import { Platform, UserRoles } from "constants/roles";
import { FormSubmitButton } from "pages/symptoms/surveyForms/FormSubmitButton";
import { FormClearButton } from "pages/symptoms/surveyForms/FormClearButton";
import {
  SurveyPage,
  SurveyContent,
  SurveyHeader,
  SurveyNoticeCard,
  SurveyQuestionCard,
  AnswerGrid,
  AnswerList,
  AnswerOption,
  SurveyFooter,
  SurveyProgressBar,
  FooterActions,
  FormItemStyle
} from "pages/symptoms/surveyForms/surveyFormComponents";

interface AppetiteImpairmentFormProps {
  setShowSurveyForm(show: boolean): void;
  selectedItems: string[];
  goToNextFormOrReturn(): void;
}

export const AppetiteImpairmentForm: React.FC<AppetiteImpairmentFormProps> = ({
  setShowSurveyForm,
  selectedItems,
  goToNextFormOrReturn
}) => {
  const params = new URLSearchParams(window.location.search);
  const platform = params.get("platform");
  const [currentPage, setCurrentPage] = useState(1);
  const [showSubmit, setShowSubmit] = useState(false);

  const [form] = Form.useForm();

  const userContext = useContext<UserContextState>(UserContext);
  const patientContext = useContext<PatientContextState>(PatientContext);

  const userRole = sessionStorage.getItem("userRole");

  const {
    isLoading: isLoadingAppetiteImpairmentMutation,
    mutate: submitAppetiteImpairmentForm
  } = useMutation<string>(
    "add-bill",
    async () => {
      return await createApiClient().addAppetiteImpairmentFormData({
        patient_internal_id:
          userRole === UserRoles.PATIENT && userContext.userData
            ? userContext.userData?.internal_id
            : patientContext?.patientData?.id || "",
        appetite: localStorage.getItem("hasReducedAppetite") || "",
        level: localStorage.getItem("levelOfAppetiteImpairment") || "",
        submitted_by: `${userContext.userData?.firstName} ${userContext.userData?.lastName}`
      });
    },
    {
      onSuccess: () => {
        message.success("Form Submitted Successfully");
        goToNextFormOrReturn();
      },
      onError: () => {
        message.error({
          content: (
            <span>
              There was a problem submitting this form, resubmit or{" "}
              <a onClick={() => goToNextFormOrReturn()}>
                click here to continue to the next survey
              </a>
            </span>
          ),
          style: {
            bottom: "10px",
            position: "fixed",
            left: "30%"
          },
          duration: 5
        });
      }
    }
  );

  const onFinish = () => {
    //Values to be passed on to API from session storage
    if (localStorage.getItem("hasReducedAppetite") === "No") {
      form.resetFields(["Level of appetite impairment"]);
      localStorage.setItem("levelOfAppetiteImpairment", "");
      submitAppetiteImpairmentForm();
    } else if (currentPage === 1) {
      setCurrentPage(2);
    } else {
      submitAppetiteImpairmentForm();
    }
  };

  const onClear = () => {
    if (currentPage === 1) {
      form.resetFields(["Appetite Impairment"]);
    } else {
      form.resetFields(["Level of appetite impairment"]);
    }
  };

  return (
    <SurveyPage>
      <div
        style={{
          marginBottom: "15px",
          display: platform === Platform.MOBILE ? "none" : "block"
        }}
      >
        <AppButton
          type={ButtonType.Primary}
          onClick={() => setShowSurveyForm(false)}
          buttonContent={
            <>
              <ArrowLeftOutlined /> Back
            </>
          }
        />
      </div>

      <SurveyContent>
        <Form
          name="appetiteImpairment"
          form={form}
          onFinish={onFinish}
          scrollToFirstError
          style={{ width: "100%" }}
        >
          <SurveyHeader title={`Survey - ${selectedItems[0]}`} />

          {currentPage === 1 && (
            <>
              <SurveyNoticeCard />

              <SurveyQuestionCard
                title="Have you had reduced appetite in the past one month?"
                required
              >
                <FormItemStyle
                  name="Appetite Impairment"
                  label=""
                  rules={[{ required: true }]}
                  initialValue=""
                >
                  <Radio.Group
                    value={localStorage.getItem("hasReducedAppetite")}
                    onChange={e => {
                      if (e.target.value === "No") {
                        setShowSubmit(true);
                      } else {
                        setShowSubmit(false);
                      }
                      localStorage.setItem(
                        "hasReducedAppetite",
                        e.target.value
                      );
                    }}
                    style={{ width: "100%" }}
                  >
                    <AnswerGrid>
                      <AnswerOption>
                        <Radio value="Yes">Yes</Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="No">No</Radio>
                      </AnswerOption>
                    </AnswerGrid>
                  </Radio.Group>
                </FormItemStyle>
              </SurveyQuestionCard>
            </>
          )}

          {currentPage === 2 && (
            <SurveyQuestionCard title="Do you have" required>
              <FormItemStyle
                name="Level of appetite impairment"
                label=""
                rules={[{ required: true }]}
                initialValue=""
              >
                <Radio.Group
                  value={localStorage.getItem("levelOfAppetiteImpairment")}
                  onChange={e =>
                    localStorage.setItem(
                      "levelOfAppetiteImpairment",
                      e.target.value
                    )
                  }
                  style={{ width: "100%" }}
                >
                  <AnswerList>
                    <AnswerOption>
                      <Radio
                        value={
                          "Mildly reduced appetite (rarely missing meals, eating about ¾ of usual)"
                        }
                      >
                        Mildly reduced appetite (rarely missing meals, eating
                        about ¾ of usual)
                      </Radio>
                    </AnswerOption>
                    <AnswerOption>
                      <Radio
                        value={
                          "Moderately reduced appetite (frequently missing meals/missing at least one meal a day, eating about ½ of usual)"
                        }
                      >
                        Moderately reduced appetite (frequently missing
                        meals/missing at least one meal a day, eating about ½ of
                        usual)
                      </Radio>
                    </AnswerOption>
                    <AnswerOption>
                      <Radio
                        value={
                          "Severely reduced appetite (mostly missing meals/no desire for food, eating about ¼ of usual)"
                        }
                      >
                        Severely reduced appetite (mostly missing meals/no
                        desire for food, eating about ¼ of usual)
                      </Radio>
                    </AnswerOption>
                    <AnswerOption>
                      <Radio value="No appetite or intake of food">
                        No appetite or intake of food
                      </Radio>
                    </AnswerOption>
                  </AnswerList>
                </Radio.Group>
              </FormItemStyle>
            </SurveyQuestionCard>
          )}

          <SurveyFooter>
            <SurveyProgressBar currentPage={currentPage} totalPages={2} />

            <FooterActions>
              <Form.Item>
                <FormClearButton onClear={onClear} />
              </Form.Item>

              {currentPage !== 1 && (
                <AppButton
                  type={ButtonType.Primary}
                  buttonContent={
                    <>
                      <ArrowLeftOutlined /> Back
                    </>
                  }
                  onClick={() => setCurrentPage(1)}
                  style={{ width: "105px" }}
                />
              )}

              <Form.Item>
                <FormSubmitButton
                  type={ButtonType.Primary}
                  buttonContent={
                    currentPage !== 1 || showSubmit ? (
                      "Submit"
                    ) : (
                      <>
                        Next <ArrowRightOutlined />
                      </>
                    )
                  }
                  htmlType="submit"
                  loading={isLoadingAppetiteImpairmentMutation}
                />
              </Form.Item>
            </FooterActions>
          </SurveyFooter>
        </Form>
      </SurveyContent>
    </SurveyPage>
  );
};
